import React from "react"
import lazyWithRetry from "./utilities/lazyWithRetry"
import { Navigate, Route, createBrowserRouter, createRoutesFromElements, useParams } from "react-router-dom"
import AppLayout from "./AppLayout"
import { AuthenticationErrorWrapper } from "./components/Auth0/authentication-error-wrapper.jsx"
import { AuthenticationGuard } from "./components/Auth0/authentication-guard.jsx"
import ErrorPage from "./components/Errors/error-page"
import LoginAuth0 from "./components/User/Login/LoginAuth0"
import ExercisesHome from "./components/game/Home/Exercises"
import GameHome from "./components/game/Home/Home"
import { InviteHandler } from "./components/Auth0/invite-handler"
import { IncidentInviteHandler } from "./components/Auth0/incident-invite-handler"
import { SetPassword } from "./components/User/Login/SetPassword"
import { ForgotPassword } from "./components/User/Login/ForgotPassword"
import { Auth0ProviderWithNavigate } from "./context/auth0/auth0-provider-with-navigate"
import UserState from "./context/user/user-state"
import { ROUTE_PATHS, SETTINGS_TABS } from "./utilities/constants"
import LegacyCompareGroupsRedirect from "./components/Metrics/CompareGroups/LegacyCompareGroupsRedirect"
import { withIntegrationsEntitlement } from "./components/Integrations/IntegrationsGate"

const CompanyHome = lazyWithRetry(() => import("./components/company/Home/Home"))
const CompanyOnboarding = lazyWithRetry(() => import("./components/company/Details/onboarding/Onboarding"))
const CompanyEmailSetup = lazyWithRetry(() => import("./components/company/EmailSetup/emailSetup"))
const SecurityExerciseSettings = lazyWithRetry(() => import("./components/company/Settings/SecurityExerciseSettings.jsx"))
const AddNewGame = lazyWithRetry(() => import("./components/game/newGame/newGame"))
const EditGame = lazyWithRetry(() => import("./components/game/editGame"))
const RestartGame = lazyWithRetry(() => import("./components/game/restartGame"))
const DuplicateGame = lazyWithRetry(() => import("./components/game/duplicateGame"))
const Playboard = lazyWithRetry(() => import("./components/game/playboard/playboard"))
const PlayboardRefactor = lazyWithRetry(() => import("./components/game/playboard/PlayboardRefactor"))
const GameplayDisplay = lazyWithRetry(() => import("./components/game/playboard/GameplayDisplay"))
const FacilitatorView = lazyWithRetry(() => import("./components/game/playboard/FacilitatorView/FacilitatorView"))
const AiTest = lazyWithRetry(() => import("./components/game/playboard/AiTest/AiTest"))
const GroupDashboard = lazyWithRetry(() => import("./components/Dashboard/GroupDashboard"))
const OrgDashboard = lazyWithRetry(() => import("./components/Metrics/OrgDashboard.jsx"))
const DashboardDetails = lazyWithRetry(() => import("./components/Dashboard/DashboardDetails/DashboardDetails.jsx"))
const RecommendationsDetailList = lazyWithRetry(
  () => import("./components/Dashboard/DashboardCard/RecommendationSection/RecommendationsDetailList.jsx")
)
const ActionDetailView = lazyWithRetry(() => import("./components/Dashboard/DashboardCard/ActionSection/ActionDetailView.jsx"))
const ActionRecommendDetailView = lazyWithRetry(() => import("./components/Dashboard/DashboardCard/ActionSection/ActionRecommendDetailView.jsx"))
const DetailsAST = lazyWithRetry(() => import("./components/Dashboard/DashboardDetails/AST.jsx"))
const IndividualPerformanceDetailView = lazyWithRetry(
  () => import("./components/Dashboard/DashboardCard/IndividualPerformanceSection/IndividualPerformanceDetailView.jsx")
)
const Report = lazyWithRetry(() => import("./components/Report/Report.jsx"))
const NewReport = lazyWithRetry(() => import("./components/Report/NewReport.jsx"))
// CTF reports spec §2/§3/§6: CTF player + event reports; ReportRouter sends old /report links for
// CTF exercises to the CTF report and renders the unchanged tabletop NewReport otherwise.
const ReportRouter = lazyWithRetry(() => import("./components/Report/ctf/CtfReportPages.jsx").then((m) => ({ default: m.ReportRouter })))
const CtfPlayerReportPage = lazyWithRetry(() =>
  import("./components/Report/ctf/CtfReportPages.jsx").then((m) => ({ default: m.CtfPlayerReportPage }))
)
const CtfEventReportPage = lazyWithRetry(() => import("./components/Report/ctf/CtfReportPages.jsx").then((m) => ({ default: m.CtfEventReportPage })))
const PeoplePage = lazyWithRetry(() => import("./components/People/PeoplePage.jsx"))
const PersonPage = lazyWithRetry(() => import("./components/People/PersonPage.jsx"))
const MyProgressPage = lazyWithRetry(() => import("./components/People/MyProgressPage.jsx"))
const CompareGroupsPage = lazyWithRetry(() => import("./components/Metrics/CompareGroups/CompareGroupsPage.jsx"))
const AdminSettings = lazyWithRetry(() => import("./components/User/Settings/AdminSettings.jsx"))

// New Scenario Builder Components
const ScenarioDashboard = lazyWithRetry(() => import("./builder/pages/Dashboard.js"))
const ScenarioCustomization = lazyWithRetry(() => import("./builder/pages/Customization.js"))
const ScenarioGenerator = lazyWithRetry(() => import("./builder/GeneratorWithContext.js"))
const BuilderShell = lazyWithRetry(() => import("./builder/fullscreen/BuilderShell.jsx"))
const ScenarioUploadTemplate = lazyWithRetry(() => import("./builder/pages/admin/ScenarioUpload.js"))
const ScenarioTemplates = lazyWithRetry(() => import("./builder/pages/admin/ScenariosList.js"))
// Manager settings: Update Prompts + Prompt Config are now composed as tabs inside
// PromptProfilesSettings (which lazy-loads each page itself). The old standalone
// /scenario/prompts and /scenario/prompt-config routes redirect here.
const PromptProfilesSettings = lazyWithRetry(() => import("./builder/pages/admin/PromptProfilesSettings.jsx"))
const ScenarioGenerationList = lazyWithRetry(() => import("./builder/pages/admin/ScenarioGenerationList.js"))
const ScenariosForTemplate = lazyWithRetry(() => import("./builder/pages/admin/TemplateScenariosList.js"))
const Evaluations = lazyWithRetry(() => import("./builder/pages/admin/Evaluations.jsx"))
const EvaluationRun = lazyWithRetry(() => import("./builder/pages/admin/EvaluationRun.jsx"))

// New Screens for Scenario Listing and customization
const ScenariosPage = lazyWithRetry(() => import("./components/Scenarios/ScenariosPage.jsx"))

// Integration Hub — connector grid for scenario grounding (sample connectors)
const IntegrationHub = lazyWithRetry(() => import("./components/Integrations/IntegrationHub.jsx"))
// Entitlement-gated like its nav link: a direct URL without INTEGRATIONS goes home.
const IntegrationHubPage = withIntegrationsEntitlement(IntegrationHub)

// Settings Wrapper
const Settings = lazyWithRetry(() => import("./components/Settings/Settings.jsx"))
const Organizations = lazyWithRetry(() => import("./components/Organizations/Organizations.jsx"))
const OrganizationDetail = lazyWithRetry(() => import("./components/Organizations/OrganizationDetail.jsx"))
const SponsorManagementPage = lazyWithRetry(() => import("./components/company/Settings/UserManagement.jsx"))

// Security Settings (Enterprise Auth)
const SecuritySettings = lazyWithRetry(() => import("./components/company/Settings/SecuritySettings.jsx"))
const SSOSettings = lazyWithRetry(() => import("./components/company/Settings/SSOSettings.jsx"))
const MFASettings = lazyWithRetry(() => import("./components/company/Settings/MFASettings.jsx"))

// Incidents
const IncidentList = lazyWithRetry(() => import("./components/incidents/IncidentList.jsx"))
const IncidentCreateDialog = lazyWithRetry(() => import("./components/incidents/IncidentCreateDialog.jsx"))
const IncidentDetail = lazyWithRetry(() => import("./components/incidents/IncidentDetail.jsx"))
const IncidentAudit = lazyWithRetry(() => import("./components/incidents/IncidentAudit.jsx"))
const HelpCenterPage = lazyWithRetry(() => import("./components/Help/HelpCenterPage.jsx"))

// Old per-simulation edit URLs carry the id in the path — forward it to the
// full-screen builder (Navigate can't interpolate params on its own).
const LegacyEditRedirect = () => {
  const { simulationId } = useParams()
  return <Navigate to={`${ROUTE_PATHS.SCENARIO_BUILD}/${simulationId}`} replace />
}

const LegacyGeneratorRedirect = () => <Navigate to={ROUTE_PATHS.SCENARIO_BUILD} replace />

// DEV-ONLY: visual harness for the CTF play screens (Playwright screenshots). The NODE_ENV guard is
// constant-folded by webpack, so production builds contain neither the route nor the chunk.
const CtfHostConsole = React.lazy(() => import("./components/game/ctf/live/CtfHostConsole"))
const CtfPlayFixturesPage =
  process.env.NODE_ENV !== "production" ? React.lazy(() => import("./components/game/ctf/play/dev/CtfPlayFixturesPage")) : null
const CtfReportFixturesPage =
  process.env.NODE_ENV !== "production" ? React.lazy(() => import("./components/Report/ctf/dev/CtfReportFixturesPage")) : null

const AppRoutes = () => {
  const basename = process.env.PUBLIC_URL ?? "/"
  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Auth0ProviderWithNavigate />} errorElement={<ErrorPage />}>
        {/* Invite route — outside AppLayout (no sidebar/header needed) */}
        <Route path="/invite" element={<InviteHandler />} />
        {CtfReportFixturesPage && (
          <Route
            path="/__dev/ctf-report"
            element={
              <React.Suspense fallback={null}>
                <CtfReportFixturesPage />
              </React.Suspense>
            }
          />
        )}
        {CtfPlayFixturesPage && (
          <Route
            path="/__dev/ctf-play"
            element={
              <React.Suspense fallback={null}>
                <CtfPlayFixturesPage />
              </React.Suspense>
            }
          />
        )}
        {/* Incident invite route — also outside AppLayout (the invitee may not
            be logged in yet). Static path beats the /incidents/:incidentKey
            param route in v6 ranking, so there is no collision. */}
        <Route path={ROUTE_PATHS.INCIDENT_INVITE} element={<IncidentInviteHandler />} />
        {/* Set-password landing page — outside AppLayout and outside any auth guard:
            the invited admin has no account password yet, so they cannot be logged in. */}
        <Route path={ROUTE_PATHS.SET_PASSWORD} element={<SetPassword />} />
        {/* "Forgot password?" from the Keycloak sign-in page — public for the same reason. */}
        <Route path={ROUTE_PATHS.FORGOT_PASSWORD} element={<ForgotPassword />} />

        <Route element={<UserState />}>
          {/* Bot screen-share of the live board — inside UserState (the board needs
              UserContext) but OUTSIDE AuthenticationGuard/AppLayout: no Auth0 login, no
              chrome. Auth is the short-lived session-scoped role-assumption JWT in ?token=
              (GameplayDisplay installs it + shims Auth0). Bot-only, read-only. */}
          <Route path={ROUTE_PATHS.GAME_ROOT + "/:gameSessionId/display"} element={<GameplayDisplay />} />
          {/* Live CTF host console + presenter view — full screen (no app sidebar), staff only. */}
          <Route
            path={ROUTE_PATHS.GAME_CTF_HOST}
            element={
              <React.Suspense fallback={null}>
                <AuthenticationGuard component={CtfHostConsole} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />
              </React.Suspense>
            }
          />
          {/* Full-screen builder — inside UserState (auth/org context) but outside
              AppLayout so it renders without the app sidebar chrome */}
          <Route
            path={ROUTE_PATHS.SCENARIO_BUILD}
            element={<AuthenticationGuard component={BuilderShell} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
          />
          <Route
            path={ROUTE_PATHS.SCENARIO_BUILD + "/:simulationId"}
            element={<AuthenticationGuard component={BuilderShell} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
          />
          {/* Full-screen exercise wizard (add/edit/restart/duplicate) — same
              full-screen treatment as the scenario builder; guards unchanged */}
          <Route path={ROUTE_PATHS.GAME_ADD_NEW} element={<AuthenticationGuard component={AddNewGame} allowTrainerRole={true} />} />
          <Route path={ROUTE_PATHS.GAME_EDIT + "/:gameSessionId"} element={<AuthenticationGuard component={EditGame} allowTrainerRole={true} />} />
          <Route
            path={ROUTE_PATHS.GAME_RESTART + "/:gameSessionId"}
            element={<AuthenticationGuard component={RestartGame} allowTrainerRole={true} />}
          />
          <Route
            path={ROUTE_PATHS.GAME_DUPLICATE + "/:gameSessionId"}
            element={<AuthenticationGuard component={DuplicateGame} allowTrainerRole={true} />}
          />
          <Route element={<AppLayout />}>
            <Route path={ROUTE_PATHS.ROOT} index={true} element={<LoginAuth0 />} />
            {/* Org readiness dashboard (metrics) — sponsor/manager, mirrors the
                backend action's role grants */}
            <Route
              path={ROUTE_PATHS.DASHBOARD}
              element={<AuthenticationGuard component={OrgDashboard} allowSponsorRole={true} allowTrainerRole={false} allowUserRole={false} />}
            />
            <Route path={ROUTE_PATHS.COMPANY_ROOT} element={<AuthenticationGuard component={CompanyHome} allowTrainerRole={true} />} />
            <Route path={ROUTE_PATHS.COMPANY_HOME} element={<AuthenticationGuard component={CompanyHome} allowTrainerRole={true} />} />
            {/* <Route path={ROUTE_PATHS.COMPANY_DETAILS} element={<AuthenticationGuard component={CompanyDetails} />} /> */}
            <Route path={ROUTE_PATHS.COMPANY_DETAILS} element={<AuthenticationGuard component={CompanyOnboarding} />} />
            <Route path={ROUTE_PATHS.COMPANY_EMAIL_SETUP} element={<AuthenticationGuard component={CompanyEmailSetup} />} />
            <Route path={ROUTE_PATHS.COMPANY_EMAIL_SETUP} element={<AuthenticationGuard component={CompanyEmailSetup} />} />
            <Route
              path={ROUTE_PATHS.COMPANY_SETTINGS}
              element={
                <AuthenticationGuard component={SecurityExerciseSettings} allowSponsorRole={true} allowTrainerRole={false} allowUserRole={false} />
              }
            />
            <Route
              path={ROUTE_PATHS.GAME_ROOT}
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={ExercisesHome} allowTrainerRole={true} allowUserRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            <Route
              path="/games-old"
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={GameHome} allowTrainerRole={true} allowUserRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            {/* The original step-by-step builder was removed; old links open the current builder. */}
            <Route
              path={ROUTE_PATHS.BUILDER_NEW}
              element={
                <AuthenticationGuard component={LegacyGeneratorRedirect} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />
              }
            />
            {/* New Scenario Builder Routes */}
            <Route
              path={ROUTE_PATHS.SCENARIO}
              element={<AuthenticationGuard component={ScenarioDashboard} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
            />
            {/* Legacy generator/edit entry points → full-screen builder.
                The redirects render nothing, but they carry the same guards as the
                destination so auth is enforced (and visible) at the legacy URL too. */}
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.GENERATOR + "/*"}
              element={
                <AuthenticationGuard component={LegacyGeneratorRedirect} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />
              }
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.CUSTOMIZE + "/*"}
              element={
                <AuthenticationGuard component={ScenarioCustomization} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />
              }
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.CUSTOMIZER + "/*"}
              element={<AuthenticationGuard component={ScenarioGenerator} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/edit/:simulationId/*"}
              element={<AuthenticationGuard component={LegacyEditRedirect} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
            />
            {/* Scenario admin routes — manager only */}
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.UPLOAD_TEMPLATE}
              element={
                <AuthenticationGuard component={ScenarioUploadTemplate} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />
              }
            />
            {/* Manager settings — tabbed Update Prompts + Prompt Config. Manager-only
                (allowManagerRole defaults true); same protection as the old routes. */}
            <Route
              path={ROUTE_PATHS.SETTINGS_PROMPT_PROFILES}
              element={
                <AuthenticationGuard component={PromptProfilesSettings} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />
              }
            />
            {/* LLM Evals — manager-only prompt-comparison page */}
            <Route
              path={ROUTE_PATHS.EVALUATIONS}
              element={<AuthenticationGuard component={Evaluations} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />}
            />
            {/* Detail page for one eval run (opened from the list's "view →") */}
            <Route
              path={ROUTE_PATHS.EVALUATION_RUN}
              element={<AuthenticationGuard component={EvaluationRun} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />}
            />
            {/* Legacy deep links redirect into the tabbed page with the matching tab. */}
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.PROMPTS}
              element={<Navigate to={`${ROUTE_PATHS.SETTINGS_PROMPT_PROFILES}?tab=${SETTINGS_TABS.UPDATE_PROMPTS}`} replace />}
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.PROMPT_CONFIG}
              element={<Navigate to={`${ROUTE_PATHS.SETTINGS_PROMPT_PROFILES}?tab=${SETTINGS_TABS.PROMPT_CONFIG}`} replace />}
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.TEMPLATES}
              element={<AuthenticationGuard component={ScenarioTemplates} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />}
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.GENERATED_SCENARIOS}
              element={
                <AuthenticationGuard component={ScenarioGenerationList} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />
              }
            />
            <Route
              path={ROUTE_PATHS.SCENARIO + "/" + ROUTE_PATHS.SCENARIO_DETAILS.GENERATED_SCENARIOS + "/:scenarioId"}
              element={
                <AuthenticationGuard component={ScenariosForTemplate} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />
              }
            />
            {/* New Route for Scenario Listing */}
            <Route
              path={ROUTE_PATHS.SCENARIOS}
              element={<AuthenticationGuard component={ScenariosPage} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
            />
            <Route
              path={ROUTE_PATHS.GAME_FACILITATOR_VIEW}
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={FacilitatorView} allowTrainerRole={true} allowUserRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            <Route
              path={ROUTE_PATHS.GAME_ROOT + "/:gameSessionId"}
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={Playboard} allowTrainerRole={true} allowUserRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            <Route
              path={ROUTE_PATHS.GAME_ROOT + "/:gameSessionId/new-experience"}
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={PlayboardRefactor} allowTrainerRole={true} allowUserRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            {/* Per-game AI test chat — tune Tracy's grounded facilitator answers by text.
                Facilitator-only (answer mode is sponsor/manager/trainer per the copilot RBAC). */}
            <Route
              path={ROUTE_PATHS.GAME_ROOT + "/:gameSessionId/ai-test"}
              element={
                <AuthenticationErrorWrapper>
                  <AuthenticationGuard component={AiTest} allowTrainerRole={true} />
                </AuthenticationErrorWrapper>
              }
            />
            <Route
              path={ROUTE_PATHS.DASHBOARD + "/:gameSessionId"}
              element={<AuthenticationGuard component={GroupDashboard} allowTrainerRole={true} />}
            />
            <Route path={ROUTE_PATHS.DASHBOARD_DETAILS} element={<AuthenticationGuard component={DashboardDetails} allowTrainerRole={true} />}>
              {/* By defualt it would route back to dashboard */}
              <Route index element={<Navigate to={ROUTE_PATHS.DASHBOARD} />} />
              <Route
                path={ROUTE_PATHS.DETAILS.RECOMMENDATIONS}
                element={<AuthenticationGuard component={RecommendationsDetailList} allowTrainerRole={true} />}
              />
              <Route path={ROUTE_PATHS.DETAILS.ACTIONS_LIST} element={<AuthenticationGuard component={ActionDetailView} allowTrainerRole={true} />} />
              <Route
                path={ROUTE_PATHS.DETAILS.ACTIONS_LIST_WITH_TRENDS + "/:trendId"}
                element={<AuthenticationGuard component={ActionDetailView} allowTrainerRole={true} />}
              />
              <Route
                path={ROUTE_PATHS.DETAILS.RECOMMENDATION_ACTION}
                element={<AuthenticationGuard component={ActionRecommendDetailView} allowTrainerRole={true} />}
              />
              {/* For unknown path, it would route back to group-dashboard */}
              <Route path="*" element={<Navigate to={ROUTE_PATHS.GAME_ROOT} />} />
            </Route>
            <Route path={ROUTE_PATHS.GROUP_DASHBOARD} element={<AuthenticationGuard component={GroupDashboard} allowTrainerRole={true} />} />
            <Route
              path={ROUTE_PATHS.GROUP_DASHBOARD + "/:groupName"}
              element={<AuthenticationGuard component={GroupDashboard} allowTrainerRole={true} />}
            />
            <Route path={ROUTE_PATHS.GROUP_DASHBOARD_DETAILS} element={<AuthenticationGuard component={DashboardDetails} allowTrainerRole={true} />}>
              {/* By defualt it would route back to group-dashboard */}
              <Route index element={<Navigate to={ROUTE_PATHS.GROUP_DASHBOARD} />} />
              <Route
                path={ROUTE_PATHS.DETAILS.AVERAGE_SIMULATION_TIME}
                element={<AuthenticationGuard component={DetailsAST} allowTrainerRole={true} />}
              />
              <Route
                path={ROUTE_PATHS.DETAILS.RECOMMENDATIONS}
                element={<AuthenticationGuard component={RecommendationsDetailList} allowTrainerRole={true} />}
              />
              <Route path={ROUTE_PATHS.DETAILS.ACTIONS_LIST} element={<AuthenticationGuard component={ActionDetailView} allowTrainerRole={true} />} />
              <Route
                path={ROUTE_PATHS.DETAILS.ACTIONS_LIST_WITH_TRENDS + "/:trendGroupName"}
                element={<AuthenticationGuard component={ActionDetailView} allowTrainerRole={true} />}
              />
              <Route
                path={ROUTE_PATHS.DETAILS.RECOMMENDATION_ACTION}
                element={<AuthenticationGuard component={ActionRecommendDetailView} allowTrainerRole={true} />}
              />
              <Route
                path={ROUTE_PATHS.DETAILS.INDIVIDUAL_PERFORMANCE}
                element={<AuthenticationGuard component={IndividualPerformanceDetailView} allowTrainerRole={true} />}
              />
              <Route
                path={ROUTE_PATHS.DETAILS.INDIVIDUAL_PERFORMANCE_WITH_TRENDS + "/:trendGroupName"}
                element={<AuthenticationGuard component={IndividualPerformanceDetailView} allowTrainerRole={true} />}
              />
              {/* For unknown path, it would route back to group-dashboard */}
              <Route path="*" element={<Navigate to={ROUTE_PATHS.GAME_ROOT} />} />
            </Route>
            <Route path={ROUTE_PATHS.REPORT} element={<AuthenticationGuard component={NewReport} allowTrainerRole={true} />} />
            {/* KPI P2: People / Person (sponsor, manager, trainer), My progress (players), Compare departments (sponsor, manager). */}
            <Route path={ROUTE_PATHS.PEOPLE} element={<AuthenticationGuard component={PeoplePage} allowTrainerRole={true} />} />
            <Route path={ROUTE_PATHS.PEOPLE + "/:personRef"} element={<AuthenticationGuard component={PersonPage} allowTrainerRole={true} />} />
            <Route
              path={ROUTE_PATHS.MY_PROGRESS}
              element={<AuthenticationGuard component={MyProgressPage} allowUserRole={true} allowSponsorRole={false} allowManagerRole={false} />}
            />
            <Route path={ROUTE_PATHS.COMPARE_DEPARTMENTS} element={<AuthenticationGuard component={CompareGroupsPage} />} />
            <Route path={ROUTE_PATHS.COMPARE_GROUPS} element={<LegacyCompareGroupsRedirect />} />
            <Route
              path={ROUTE_PATHS.REPORT + "/:gameSessionId"}
              element={<AuthenticationGuard component={ReportRouter} allowTrainerRole={true} allowUserRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.GROUP_GAME_REPORT + "/:groupName/:gameSessionId"}
              element={<AuthenticationGuard component={ReportRouter} allowTrainerRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.CTF_REPORT + "/:gameSessionId"}
              element={<AuthenticationGuard component={CtfPlayerReportPage} allowTrainerRole={true} allowUserRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.CTF_EVENT_REPORT + "/:groupName"}
              element={<AuthenticationGuard component={CtfEventReportPage} allowTrainerRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.CTF_EVENT_REPORT + "/:groupName/q/:questionId"}
              element={<AuthenticationGuard component={CtfEventReportPage} allowTrainerRole={true} />}
            />
            <Route path={ROUTE_PATHS.OLD_REPORT} element={<AuthenticationGuard component={Report} allowTrainerRole={true} />} />
            <Route
              path={ROUTE_PATHS.OLD_REPORT + "/:gameSessionId"}
              element={<AuthenticationGuard component={Report} allowTrainerRole={true} allowUserRole={true} />}
            />
            {/* Admin Routes — manager only */}
            <Route
              path={ROUTE_PATHS.ADMIN_SETTINGS}
              element={<AuthenticationGuard component={AdminSettings} allowSponsorRole={false} allowTrainerRole={false} allowUserRole={false} />}
            />

            {/* Partner org / child-customer management (managers + partner-entitled sponsors) */}
            <Route
              path={ROUTE_PATHS.ORGANIZATIONS}
              element={<AuthenticationGuard component={Organizations} allowManagerRole={true} allowSponsorRole={true} />}
            />
            <Route
              path={`${ROUTE_PATHS.ORGANIZATIONS}/:orgId`}
              element={<AuthenticationGuard component={OrganizationDetail} allowManagerRole={true} allowSponsorRole={true} />}
            />

            {/* Integration Hub — sponsor/manager (mirrors dashboard/admin scoping), INTEGRATIONS entitlement only.
                One route for the grid and a tool's page (/integrations/<tool>), so the loaded data stays. */}
            <Route
              path={ROUTE_PATHS.INTEGRATIONS_ROUTE}
              element={<AuthenticationGuard component={IntegrationHubPage} allowSponsorRole={true} allowTrainerRole={false} allowUserRole={false} />}
            />

            {/* Help Center — task-organized help hub + walkthroughs, for every signed-in role */}
            <Route
              path={ROUTE_PATHS.HELP}
              element={<AuthenticationGuard component={HelpCenterPage} allowTrainerRole={true} allowUserRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.HELP_TOPIC}
              element={<AuthenticationGuard component={HelpCenterPage} allowTrainerRole={true} allowUserRole={true} />}
            />
            {/* Settings Wrapper */}
            <Route path={ROUTE_PATHS.SETTINGS} element={<AuthenticationGuard component={Settings} allowTrainerRole={true} allowUserRole={true} />} />
            <Route
              path={ROUTE_PATHS.COMPANY_USER_MANAGEMENT}
              element={
                <AuthenticationGuard
                  component={SponsorManagementPage}
                  allowSponsorRole={true}
                  allowTrainerRole={false}
                  allowUserRole={false}
                  allowManagerRole={false}
                />
              }
            />

            {/* Security Settings (Enterprise Auth) */}
            <Route
              path={ROUTE_PATHS.SECURITY_SETTINGS}
              element={<AuthenticationGuard component={SecuritySettings} allowSponsorRole={true} allowUserRole={false} />}
            />
            <Route
              path={ROUTE_PATHS.SECURITY_SSO}
              element={<AuthenticationGuard component={SSOSettings} allowSponsorRole={true} allowUserRole={false} />}
            />
            <Route
              path={ROUTE_PATHS.SECURITY_MFA}
              element={<AuthenticationGuard component={MFASettings} allowSponsorRole={true} allowUserRole={false} />}
            />

            {/* Incidents (M1) — placeholder pages so routes resolve while feature work lands */}
            <Route
              path={ROUTE_PATHS.INCIDENTS}
              element={<AuthenticationGuard component={IncidentList} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={true} />}
            />
            <Route
              path={ROUTE_PATHS.INCIDENT_NEW}
              element={<AuthenticationGuard component={IncidentCreateDialog} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={false} />}
            />
            <Route
              path="/incidents/:incidentKey"
              element={<AuthenticationGuard component={IncidentDetail} allowSponsorRole={true} allowTrainerRole={true} allowUserRole={true} />}
            />
            <Route
              path="/incidents/:incidentKey/audit"
              element={
                <AuthenticationGuard
                  component={IncidentAudit}
                  allowSponsorRole={true}
                  allowManagerRole={true}
                  allowTrainerRole={true}
                  allowUserRole={true}
                />
              }
            />

            <Route
              path="*"
              element={<ErrorPage title={`We Couldn't Find That Page`} message={`The URL you visited is not valid or no longer valid.`} />}
            />
          </Route>
        </Route>
      </Route>
    ),
    { basename } // basename allows hosting at different (sub folder) paths depending on environment: DEV, TEST, PROD, etc.
  )

  return router
}

export default AppRoutes
